import type { LeafPrimitive, RegionBorders, Template, Zone } from '@tsagko/label-contracts'
import { splitChildBoxes } from './split'
import { projectPlacement } from './place-baked-instance'

export interface FlatZone {
  path: string[]
  xMm: number
  yMm: number
  widthMm: number
  heightMm: number
  /**
   * Always a leaf primitive — `baked-block-instance` components are expanded
   * recursively at flatten time (sub-task 2a.10.11), so downstream callers
   * such as `buildCommands` only have to dispatch on the 6 leaf shapes.
   */
  component: LeafPrimitive
  /**
   * Background color inherited from the leaf zone's `style.backgroundColor`,
   * if any. Currently the renderer only emits special ZPL for
   * `backgroundColor === 'black'` (filled `^GB` + `^FR` reverse-field on the
   * leaf's primitive); any other value is treated as transparent.
   * Undefined when the zone carries no `style` or no `backgroundColor`.
   */
  backgroundColor?: string
  /** Region borders carried verbatim from the leaf zone. Consumed by
   *  collectRegionBorders. */
  borders?: RegionBorders
}

/**
 * Maximum nesting depth for baked-block-instance recursion. Phase 1 inputs
 * are author-controlled, but a malformed template with self/mutual
 * referential baked blocks would stack-overflow the walker without an
 * explicit guard. 32 is well above any plausible authoring depth while
 * still being small enough to short-circuit quickly on a pathological
 * cycle. Counts ONLY baked-block expansions — split recursion is not
 * counted, so deeply nested splits (which can't cycle by construction)
 * are unaffected. Carry-over from the 2a.10.11 code-quality review,
 * landed in 2a.10.13.
 */
const MAX_BAKED_BLOCK_DEPTH = 32

/**
 * Walks a Template's recursive `rootZone` tree and returns a flat list of
 * leaf zones with absolute mm coordinates and root-to-leaf id paths.
 *
 * Split semantics:
 *   - `horizontal`: children[0] is the TOP half, children[1] is the BOTTOM
 *     half, separated at parent.height * ratio.
 *   - `vertical`: children[0] is the LEFT half, children[1] is the RIGHT
 *     half, separated at parent.width * ratio.
 *
 * Leaves with an `absolutePosition` override use those coordinates verbatim
 * instead of the computed box. Their path remains the root-to-leaf chain.
 *
 * Pure: does not mutate the template. Each emitted FlatZone gets a freshly
 * allocated `path` array.
 *
 * Baked-block expansion (sub-task 2a.10.11):
 *   When a leaf zone's `component.type === 'baked-block-instance'`, the
 *   walker does NOT emit the block instance itself. Instead it recurses
 *   into `component.expansion` (a `Zone` representing the block's internal
 *   layout) using the host leaf's bounding box as the new origin. The host
 *   zone's id and the block instance's id are both appended to the
 *   ancestor path so audit/lint can trace which block produced which
 *   primitive. Nested baked blocks recurse the same way — each level
 *   tacks its host-zone id and instance id onto the chain.
 *
 *   `absolutePosition` on the host zone (the leaf that holds the block
 *   instance) is honoured as the new coordinate origin for the recursion;
 *   `absolutePosition` on a child of the expansion behaves like any other
 *   leaf override.
 *
 *   The expansion's bounding box is projected from the host leaf's box
 *   according to `component.placement` (per Task 6 / spec §2): a
 *   host-filling centred placement (the migration default) yields the host
 *   box exactly, while smaller placements offset the expansion within the
 *   host. `projectPlacement` is applied AFTER any `absolutePosition`
 *   override on the host, so the projection is always relative to the
 *   effective host box.
 */
export function flattenZones(template: Template): FlatZone[] {
  const out: FlatZone[] = []
  walk(template.rootZone, 0, 0, template.widthMm, template.heightMm, [], out, 0)
  return out
}

function walk(
  zone: Zone,
  xMm: number,
  yMm: number,
  widthMm: number,
  heightMm: number,
  ancestorPath: readonly string[],
  out: FlatZone[],
  bakedBlockDepth: number
): void {
  const path = [...ancestorPath, zone.id]

  if (zone.split !== undefined && zone.children !== undefined) {
    const [a, b] = zone.children
    const [boxA, boxB] = splitChildBoxes(
      { x: xMm, y: yMm, w: widthMm, h: heightMm },
      zone.split
    )
    walk(a, boxA.x, boxA.y, boxA.w, boxA.h, path, out, bakedBlockDepth)
    walk(b, boxB.x, boxB.y, boxB.w, boxB.h, path, out, bakedBlockDepth)
    return
  }

  if (zone.component !== undefined) {
    const abs = zone.absolutePosition
    const boxX = abs ? abs.x : xMm
    const boxY = abs ? abs.y : yMm
    const boxW = abs ? abs.w : widthMm
    const boxH = abs ? abs.h : heightMm
    if (zone.component.type === 'baked-block-instance') {
      // Bounded-depth guard: Phase 1 inputs are author-controlled, but a
      // malformed template with self/mutual referential baked blocks would
      // stack-overflow the walker. We bump `bakedBlockDepth` ONLY on this
      // arm (split recursion is not counted — splits can't cycle by
      // construction) and bail with a typed error once we cross the limit.
      const nextDepth = bakedBlockDepth + 1
      if (nextDepth > MAX_BAKED_BLOCK_DEPTH) {
        // Sub-task 2a.10.15 carry-over: include the full root-to-instance
        // path so authors can pinpoint which baked block produced the
        // cycle. The path is the chain of zone ids walked so far (from
        // the template's rootZone), terminating in the offending host
        // leaf's id. The instance id is repeated at the end of the
        // message for searchability.
        throw new Error(
          `baked-block expansion exceeded max depth ${MAX_BAKED_BLOCK_DEPTH} — possible cycle at instance ${zone.component.id} (path: ${path.join(' > ')})`
        )
      }
      // Project the placement onto the host box. The expansion is laid out
      // inside the projected box, not the host. When placement fills the
      // host (the migration default produced by parseBakedBlockInstance),
      // the projected box equals the host box and behaviour matches the
      // pre-placement era. projectPlacement is applied after
      // absolutePosition, so the projection is always relative to the
      // effective host box. `placement` is required by the schema (Task 2)
      // and is unconditionally present on all baked instances reaching
      // this code path.
      const projected = projectPlacement(
        { x: boxX, y: boxY, w: boxW, h: boxH },
        zone.component.placement
      )
      walk(
        zone.component.expansion,
        projected.x,
        projected.y,
        projected.w,
        projected.h,
        path.concat(zone.component.id),
        out,
        nextDepth
      )
      return
    }
    out.push({
      path,
      xMm: boxX,
      yMm: boxY,
      widthMm: boxW,
      heightMm: boxH,
      component: zone.component,
      backgroundColor: zone.style?.backgroundColor,
      borders: zone.borders,
    })
  }
}

